import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  InputNumber,
  Row,
  Col,
  Typography,
  message,
  Select,
  Input,
  Space,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import axiosInstance from "../../../configs";

const BasePriceTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [types, setTypes] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null); // null = mode tambah, object = mode edit
  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resPrices, resTypes] = await Promise.all([
        axiosInstance.get("/referensi-finance/prices"),
        axiosInstance.get("/referensi-finance/types"),
      ]);
      setData(resPrices.data.data);
      setTypes(resTypes.data.data || []);
    } catch (err) {
      message.error("Gagal mengambil data harga");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Buka modal tambah baru
  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldsValue({ tax: 11.0, contact_type: 0 });
    setModalVisible(true);
  };

  // Buka modal edit
  const handleEdit = (record) => {
    setEditingItem(record);
    form.resetFields();
    form.setFieldsValue({
      id_type_cost: record.id_type_cost,
      cost_code: record.cost_code || "",
      cost_per_unit: record.cost,
      tax: record.tax || 11.0,
      contact_type: record.contact_type || 0,
    });
    setModalVisible(true);
  };

  // Satu fungsi untuk simpan (tambah & edit)
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (editingItem) {
        // Mode edit: payload hanya cost_code, cost_per_unit, tax
        const payload = {
          cost_code: values.cost_code || "",
          cost_per_unit: values.cost_per_unit,
          tax: String(values.tax),
        };
        await axiosInstance.put(
          `/referensi-finance/prices/${editingItem.id_cost}`,
          payload,
        );
        message.success("Harga dasar berhasil diperbarui");
      } else {
        // Mode tambah: POST biasa
        const payload = {
          id_type_cost: values.id_type_cost,
          cost_code: values.cost_code || "",
          cost_per_unit: values.cost_per_unit,
          tax: String(values.tax),
          contact_type: values.contact_type,
        };
        await axiosInstance.post("/referensi-finance/prices", payload);
        message.success("Harga dasar berhasil ditambahkan");
      }

      setModalVisible(false);
      fetchData();
    } catch (err) {
      message.error("Gagal menyimpan harga dasar");
    }
  };

  const columns = [
    { title: "Cost Code", dataIndex: "cost_code", width: "15%" },
    { title: "Layanan", dataIndex: "type_cost_name", width: "20%" },
    {
      title: "Grup",
      dataIndex: "cost_group_name",
      width: "25%",
      responsive: ["md"],
    },
    {
      title: "Harga (IDR)",
      dataIndex: "cost",
      align: "right",
      width: "12%",
      render: (val) => (val ? val.toLocaleString("id-ID") : "0"),
    },
    {
      title: "Pajak (%)",
      dataIndex: "tax",
      align: "right",
      width: "10%",
      render: (val) => (val ? val.toLocaleString("id-ID") : "0"),
    },
    {
      title: "Tipe",
      dataIndex: "contact_type",
      width: 80,
      render: (val) => (val === 0 ? "Grup" : "Non"),
    },
    {
      title: "Aksi",
      key: "action",
      width: "10%",
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          marginBottom: 12,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Daftar harga dasar untuk setiap layanan.
        </Typography.Text>
        <Button type="primary" onClick={handleAdd}>
          Tambah Harga Dasar
        </Button>
      </div>
      <Table
        dataSource={data}
        columns={columns}
        rowKey="id_type_cost"
        loading={loading}
        size="small"
        scroll={{ x: 600 }}
        pagination={{ pageSize: 10, size: "small" }}
      />
      {/* Satu Modal untuk Tambah & Edit */}
      <Modal
        title={
          editingItem
            ? `Edit Harga: ${editingItem?.type_cost_name} - ${editingItem?.cost_group_name}`
            : "Tambah Harga Dasar"
        }
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
      >
        <Form form={form} layout="vertical">
          {!editingItem && (
            <Form.Item
              name="id_type_cost"
              label="Layanan / Biaya"
              rules={[{ required: true, message: "Harap pilih layanan" }]}
            >
              <Select
                showSearch
                placeholder="Pilih Layanan"
                optionFilterProp="children"
              >
                {types.map((t) => (
                  <Select.Option key={t.id_type_cost} value={t.id_type_cost}>
                    {t.type_cost_name} - {t.cost_group_name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            name="cost_code"
            label="Cost Code"
            rules={[{ required: true, message: "Harap isi cost code" }]}
          >
            <Input placeholder="Contoh: test" />
          </Form.Item>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="cost_per_unit"
                label="Harga per Unit (IDR)"
                rules={[{ required: true, message: "Harap isi harga" }]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="tax"
                label="Pajak (%)"
                rules={[
                  { required: true, message: "Harap isi persentase pajak" },
                ]}
              >
                <InputNumber style={{ width: "100%" }} step={0.1} />
              </Form.Item>
            </Col>
          </Row>

          {!editingItem && (
            <Form.Item name="contact_type" label="Tipe Kontak">
              <Select placeholder="Pilih tipe kontak">
                <Select.Option value={0}>Grup</Select.Option>
                <Select.Option value={1}>Non Grup</Select.Option>
              </Select>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default BasePriceTable;
